import React, { useState } from 'react';
import { Modal, Alert } from 'antd';
import { AudioOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRecorder } from '../../hooks/useRecorder';
import useRecordingTimer from '../../hooks/useRecordingTimer';
import { usePreventRecordingAbandon } from '../../hooks/usePreventRecordingAbandon';
import completeSession from '../../utils/session/completeSession';
import { displayWarningModal } from '../../utils/errorHandling';

import NewSessionButton from './NewSessionButton';
import PatientSelectorPanel from '../patient/PatientSelectorPanel';
import RecordingNotesSection from './RecordingNotesSection';
import PatientForm from '../../components/patient/PatientForm';
import RecordingDiscard from './RecordingDiscard';
import RecordingTimerSection from './RecordingTimerSection';
import RecordingActions from './RecordingActions';
import MicrophoneError from './MicrophoneError';
import NoteTypeSelector from './NoteTypeSelector';
import { logEvent } from "../../analytics";
import NewSessionWithPatientButton from "./NewSessionWithPatientButton";

export default function RecordingModal({ patient = null }) {
  const { t } = useTranslation();
  const [showNoteTypeSelector, setShowNoteTypeSelector] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [recordingModalState, setRecordingModalState] = useState('initial');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isSelectingPatient, setIsSelectingPatient] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);

  const {
    elapsedTime,
    start: startTimer,
    pause: pauseTimer,
    reset: resetTimer
  } = useRecordingTimer();

  const {
    isRecording,
    toggleRecording,
    endRecording,
    sessionUuid,
    totalChunks,
    problemType,
    problemStats,
  } = useRecorder();

  usePreventRecordingAbandon(isRecording, sessionUuid);

  const handleNewSession = async () => {
    setSelectedPatient(patient); // reinitialize from prop
    setIsSessionModalOpen(true);
  };

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await toggleRecording();
      // Delay the UI state update to give the DOM time to stabilize.
      setTimeout(() => {
        // Only update the UI if the recorder is in a recording state.
        // (You may optionally check isRecording here if needed.)
        setRecordingModalState('recording');
        startTimer();
        setIsStarting(false);
      }, 10);
    } catch (error) {
      // If an error occurred (mic access denied, etc.), revert the UI immediately.
      setRecordingModalState('initial');
      setIsStarting(false);
    }
  };

  const handlePause = () => {
    toggleRecording();
    setRecordingModalState('paused');
    pauseTimer();
  };

  const handleResume = () => {
    toggleRecording();
    setRecordingModalState('recording');
    startTimer();
  };

  const handleFinalize = async () => {
    setIsFinalizing(true);
    if (!sessionUuid || totalChunks < 1 || elapsedTime < 30) {
      displayWarningModal(
        t('session_recorder.errors.too_short_title'),
        t('session_recorder.errors.too_short_body'),
        'rec-too-short-warning-modal'
      );
      setIsFinalizing(false);
      return;
    }

    const success = await completeSession(sessionUuid, selectedPatient?.getUUID(), totalChunks, problemStats);
    if (success) {
      await endRecording();
      closeModal();
      logEvent('SessionRecorder', 'Complete');
      setShowNoteTypeSelector(true);
    } else {
      displayWarningModal(
        t('session_recorder.errors.complete_title'),
        t('session_recorder.errors.complete_body'),
        'complete-failed-warning-modal'
      );
    }

    setIsFinalizing(false);
  };

  const handleCloseNoteTypeSelector = () => {
    setShowNoteTypeSelector(false);
  };

  const closeModal = async () => {
    pauseTimer();
    await endRecording();
    setRecordingModalState('initial');
    resetTimer();
    setIsSelectingPatient(false);
    setIsSessionModalOpen(false);
    setEditingPatient(null);
  };

  return (
    <>
      {patient ? (
        <NewSessionWithPatientButton onClick={handleNewSession} />
      ) : (
        <NewSessionButton onClick={handleNewSession} />
      )}

      <Modal
        // forceRender ensures that content is rendered once and kept in the DOM even when hidden. This prevents the rapid unmount/remount cycle that is likely causing the reference mismatch
        forceRender
        title={
          <>
            {recordingModalState === 'recording' && <AudioOutlined className='iconSpacing' />}
            {{
              initial: t('recording_modal.initial_title'),
              recording: t('recording_modal.recording_title'),
              paused: t('recording_modal.paused_title'),
            }[recordingModalState]}
          </>
        }
        open={isSessionModalOpen}
        onCancel={closeModal}
        footer={null}
        width={600}
        closable={false}
        maskClosable={false}
      >
        <RecordingDiscard
          recordingModalState={recordingModalState}
          sessionUuid={sessionUuid}
          endRecording={endRecording}
          onDiscarded={closeModal}
        />

        <div style={{padding: 16}}>
          <PatientSelectorPanel
            selectedPatient={selectedPatient}
            onSelect={(patient) => {
              setSelectedPatient(patient);
              setIsSelectingPatient(false);
            }}
            onAddPatient={() => {
              setEditingPatient(null);
              setIsPatientModalOpen(true);
              setIsSelectingPatient(false);
            }}
            onEditPatient={() => {
              setEditingPatient(selectedPatient);
              setIsPatientModalOpen(true);
            }}
            onUnlinkPatient={() => setSelectedPatient(null)}
          />

          <RecordingNotesSection sessionUuid={sessionUuid}/>

          {recordingModalState !== 'initial' && (
            <div>
              <RecordingTimerSection elapsedTime={elapsedTime}/>
              <MicrophoneError type={problemType}/>
            </div>
          )}

          { recordingModalState === 'recording' && !problemType && elapsedTime < 5 &&  (
            <div style={{textAlign: 'center', marginTop: 0, marginBottom: 10}}>
              <Alert type='success' message={t('session_recorder.subtitle')} />
            </div>
          )}

          <RecordingActions
            recordingModalState={recordingModalState}
            onStart={handleStart}
            onPause={handlePause}
            onResume={handleResume}
            onFinalize={handleFinalize}
            isFinalizing={isFinalizing}
            isStarting={isStarting}
          />
        </div>
      </Modal>

      <PatientForm
        isOpen={isPatientModalOpen}
        editingPatient={editingPatient}
        onCancel={() => {
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
        onSave={(savedPatient) => {
          setSelectedPatient(savedPatient);
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
      />

      {showNoteTypeSelector && (
        <NoteTypeSelector
          sessionUuid={sessionUuid}
          visible={showNoteTypeSelector}
          onClose={handleCloseNoteTypeSelector}
        />
      )}
    </>
  );
}